import { api, apiUrl, resolveTenantSlug, tokens } from './api';

// ---- public (unauthenticated) shape ----
export interface PublicAchievement {
  kind: string; title: string; body: string; highlight: string | null;
  studentName: string | null; publishedAt: string; imageUrls: string[];
}
export interface PublicGalleryItem { url: string; caption: string | null; }
export interface SiteStat { label: string; value: string; }

export interface PublicSite {
  schoolName: string; logoUrl: string | null; primaryColor: string; accentColor: string;
  heroHeadline: string | null; heroImageUrl: string | null;
  showAbout: boolean; aboutTitle: string | null; aboutBody: string | null;
  establishedYear: number | null; boardsOffered: string | null;
  showStats: boolean; stats: SiteStat[];
  admissionsOpen: boolean; admissionsNote: string | null;
  showAchievements: boolean; achievements: PublicAchievement[];
  showGallery: boolean; gallery: PublicGalleryItem[];
  showContact: boolean; contactPhone: string | null; contactEmail: string | null;
  addressLine: string | null; mapUrl: string | null;
  websiteUrl: string | null; facebookUrl: string | null; instagramUrl: string | null; youtubeUrl: string | null;
  /**
   * ⚠ EMPTY FOR A SINGLE-INSTITUTION TRUST, and the section is not rendered. A heading listing one
   * school under a page already about that school is noise. Also absent from an older server, which
   * is why every consumer must check Array.isArray rather than truthiness.
   */
  institutions?: PublicInstitution[];
}

/** One school or college as a visitor sees it. `admissionsOpen` is already resolved by the server —
 *  the institution's own answer, or the trust's when it has none. */
export interface PublicInstitution {
  id: string; name: string; typeLabel: string; boardLabel: string | null;
  campusName: string | null; city: string | null;
  blurb: string | null; phone: string | null; admissionsOpen: boolean;
}

// ---- admin shape ----
export interface PublicSiteAdmin {
  isEnabled: boolean;
  heroHeadline: string | null; heroImageUrl: string | null;
  aboutTitle: string | null; aboutBody: string | null; establishedYear: number | null; boardsOffered: string | null;
  stats: SiteStat[];
  admissionsOpen: boolean; admissionsNote: string | null;
  contactPhone: string | null; contactEmail: string | null; addressLine: string | null; mapUrl: string | null;
  websiteUrl: string | null; facebookUrl: string | null; instagramUrl: string | null; youtubeUrl: string | null;
  showAchievements: boolean; showGallery: boolean; showAbout: boolean; showStats: boolean; showContact: boolean;
}

// The public page is fetched WITHOUT auth (it renders before login). We still send the tenant header
// so the backend resolves the school, but no Authorization.
async function fetchPublic<T>(path: string): Promise<T | null> {
  const slug = resolveTenantSlug();
  const res = await fetch(apiUrl(path), { headers: slug ? { 'X-Tenant': slug } : {} });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error('Failed to load');
  return (await res.json()) as T;
}

export const publicSiteApi = {
  // public
  get: () => fetchPublic<PublicSite>('/api/public/site'),
  fileUrl: (url: string) => apiUrl(url),   // public files need no auth header on <img>

  // admin
  admin: () => api.get<PublicSiteAdmin>('/api/site/'),
  setEnabled: (on: boolean) => api.post<PublicSiteAdmin>(`/api/site/enabled/${on ? 'on' : 'off'}`, {}),
  updateHero: (headline: string | null) => api.put<PublicSiteAdmin>('/api/site/hero', { headline }),
  updateAbout: (b: { title: string | null; body: string | null; establishedYear: number | null; boards: string | null }) =>
    api.put<PublicSiteAdmin>('/api/site/about', b),
  updateStats: (stats: SiteStat[]) => api.put<PublicSiteAdmin>('/api/site/stats', { stats }),
  updateAdmissions: (open: boolean, note: string | null) => api.put<PublicSiteAdmin>('/api/site/admissions', { open, note }),
  updateContact: (b: Partial<PublicSiteAdmin>) => api.put<PublicSiteAdmin>('/api/site/contact', {
    phone: b.contactPhone ?? null, email: b.contactEmail ?? null, address: b.addressLine ?? null, mapUrl: b.mapUrl ?? null,
    website: b.websiteUrl ?? null, facebook: b.facebookUrl ?? null, instagram: b.instagramUrl ?? null, youtube: b.youtubeUrl ?? null,
  }),
  updateSections: (b: { achievements: boolean; gallery: boolean; about: boolean; stats: boolean; contact: boolean }) =>
    api.put<PublicSiteAdmin>('/api/site/sections', b),
  uploadHero: async (file: File): Promise<PublicSiteAdmin> => {
    const fd = new FormData(); fd.append('file', file);
    const slug = resolveTenantSlug();
    const res = await fetch(apiUrl('/api/site/hero-image'), {
      method: 'POST',
      headers: { ...(slug ? { 'X-Tenant': slug } : {}), ...(tokens.access() ? { Authorization: `Bearer ${tokens.access()}` } : {}) },
      body: fd,
    });
    if (!res.ok) throw new Error('Upload failed');
    return (await res.json()) as PublicSiteAdmin;
  },
};
